import React, { useEffect, useMemo, useState } from 'react';
import {
  Download,
  Globe,
  History,
  Info,
  KeyRound,
  Monitor,
  Moon,
  MoreVertical,
  Palette,
  Search,
  Shield,
  Sun,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import type { BrowserPageTheme } from './types';

type BrowserSettingsDialogProps = {
  open: boolean;
  browserTheme: BrowserPageTheme;
  homepage: string;
  portalContainer?: HTMLElement | null;
  onOpenChange: (open: boolean) => void;
  onThemeChange: (theme: BrowserPageTheme) => void | Promise<void>;
  onHomepageChange: (homepage: string) => void | Promise<void>;
  onOpenLibrary: () => void;
  onOpenPasswordManager: () => void;
};

type SettingsSectionId = 'general' | 'appearance' | 'privacy' | 'downloads' | 'about';

type SettingsSection = {
  id: SettingsSectionId;
  label: string;
  description: string;
  icon: typeof Globe;
  keywords: string[];
};

const themeOptions: Array<{
  value: BrowserPageTheme;
  label: string;
  description: string;
  icon: typeof Sun;
}> = [
  {
    value: 'light',
    label: 'Light',
    description: 'Prefer light mode on websites that follow the browser color-scheme hint.',
    icon: Sun,
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Prefer dark mode on websites that follow the browser color-scheme hint.',
    icon: Moon,
  },
  {
    value: 'system',
    label: 'System',
    description: 'Let websites follow the current browser and OS environment.',
    icon: Monitor,
  },
];

const sections: SettingsSection[] = [
  {
    id: 'general',
    label: 'General',
    description: 'Everyday browsing behavior and quick preferences.',
    icon: Globe,
    keywords: ['general', 'home', 'homepage', 'search', 'navigation', 'browser'],
  },
  {
    id: 'appearance',
    label: 'Appearance',
    description: 'How websites and MapleBrowser settings are presented.',
    icon: Palette,
    keywords: ['appearance', 'theme', 'light', 'dark', 'system', 'color'],
  },
  {
    id: 'privacy',
    label: 'Privacy & security',
    description: 'History, passwords, and browser-level website preferences.',
    icon: Shield,
    keywords: ['privacy', 'security', 'website', 'rendering', 'preferences', 'history', 'passwords', 'vault'],
  },
  {
    id: 'downloads',
    label: 'Downloads',
    description: 'Where saved files appear inside MapleOS.',
    icon: Download,
    keywords: ['downloads', 'files', 'save', 'storage'],
  },
  {
    id: 'about',
    label: 'About MapleBrowser',
    description: 'Version and browser engine details.',
    icon: Info,
    keywords: ['about', 'engine', 'electron', 'browser', 'version'],
  },
];

const settingsRowClassName = 'flex items-start justify-between gap-4 rounded-lg border border-border/70 bg-background/70 px-4 py-3';

export const BrowserSettingsDialog = ({
  open,
  browserTheme,
  homepage,
  portalContainer,
  onOpenChange,
  onThemeChange,
  onHomepageChange,
  onOpenLibrary,
  onOpenPasswordManager,
}: BrowserSettingsDialogProps) => {
  const [selectedSection, setSelectedSection] = useState<SettingsSectionId>('appearance');
  const [searchValue, setSearchValue] = useState('');
  const [draftTheme, setDraftTheme] = useState<BrowserPageTheme>(browserTheme);
  const [draftHomepage, setDraftHomepage] = useState(homepage);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDraftTheme(browserTheme);
      setDraftHomepage(homepage);
      setSaveError(null);
      setIsSaving(false);
    }
  }, [browserTheme, homepage, open]);

  const normalizedSearch = searchValue.trim().toLowerCase();
  const activeTheme = themeOptions.find((option) => option.value === draftTheme) || themeOptions[0];
  const hasPendingChanges = draftTheme !== browserTheme || draftHomepage.trim() !== homepage;

  const filteredSections = useMemo(() => {
    if (!normalizedSearch) {
      return sections.filter((section) => section.id === selectedSection);
    }

    return sections.filter((section) => {
      const haystack = [section.label, section.description, ...section.keywords].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [normalizedSearch, selectedSection]);

  const handleSectionSelect = (sectionId: SettingsSectionId) => {
    setSelectedSection(sectionId);
    setSearchValue('');
  };

  const handleCancel = () => {
    setDraftTheme(browserTheme);
    setDraftHomepage(homepage);
    setSaveError(null);
    onOpenChange(false);
  };

  const handleSave = async () => {
    setSaveError(null);
    setIsSaving(true);

    try {
      await onHomepageChange(draftHomepage);
      await onThemeChange(draftTheme);
      onOpenChange(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not save your Navigator settings.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenLibrary = () => {
    onOpenChange(false);
    onOpenLibrary();
  };

  const handleOpenPasswordManager = () => {
    onOpenChange(false);
    onOpenPasswordManager();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" size="icon" variant="outline" aria-label="Browser settings">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="h-[min(88vh,760px)] max-w-[min(96vw,1100px)] overflow-hidden p-0" portalContainer={portalContainer}>
        <DialogTitle className="sr-only">Browser settings</DialogTitle>
        <DialogDescription className="sr-only">
          Manage MapleBrowser settings, including website appearance preferences.
        </DialogDescription>

        <div className="flex h-full min-h-0 bg-background">
          <aside className="flex w-64 shrink-0 flex-col border-r border-border/70 bg-muted/30">
            <div className="px-5 py-5">
              <div className="text-xl font-semibold tracking-tight">Settings</div>
              <p className="mt-1 text-sm text-muted-foreground">MapleBrowser preferences</p>
            </div>

            <Separator />

            <ScrollArea className="flex-1 px-3 py-4">
              <nav className="space-y-1">
                {sections.map((section) => {
                  const SectionIcon = section.icon;
                  const isActive = !normalizedSearch && selectedSection === section.id;

                  return (
                    <Button
                      key={section.id}
                      type="button"
                      variant="ghost"
                      className={[
                        'h-auto w-full justify-start gap-3 rounded-xl px-3 py-3 text-left',
                        isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground',
                      ].join(' ')}
                      onClick={() => handleSectionSelect(section.id)}
                    >
                      <SectionIcon className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium">{section.label}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{section.description}</div>
                      </div>
                    </Button>
                  );
                })}
              </nav>
            </ScrollArea>
          </aside>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="border-b border-border/70 px-6 py-5">
              <div className="relative max-w-xl">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  className="h-11 rounded-full pl-10"
                  placeholder="Search settings"
                  aria-label="Search settings"
                />
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-6">
                {filteredSections.length === 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle>No settings found</CardTitle>
                      <CardDescription>Try a different search term.</CardDescription>
                    </CardHeader>
                  </Card>
                ) : null}

                {filteredSections.map((section) => {
                  if (section.id === 'general') {
                    return (
                      <div key={section.id} className="space-y-4">
                        <div>
                          <h2 className="text-2xl font-semibold tracking-tight">General</h2>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Core MapleBrowser behavior that affects everyday browsing.
                          </p>
                        </div>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Home page</CardTitle>
                            <CardDescription>Choose the website Navigator opens when you press the Home button.</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="space-y-2">
                              <Label htmlFor="navigator-homepage">Homepage URL</Label>
                              <Input
                                id="navigator-homepage"
                                value={draftHomepage}
                                onChange={(event) => setDraftHomepage(event.target.value)}
                                placeholder="https://duckduckgo.com/"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                              />
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Enter a full URL like <span className="font-medium text-foreground">https://example.com</span> or a domain like <span className="font-medium text-foreground">example.com</span>. Leave it blank to use DuckDuckGo.
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Quick overview</CardTitle>
                            <CardDescription>MapleBrowser defaults for search, navigation, and website appearance.</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className={settingsRowClassName}>
                              <div>
                                <div className="font-medium">Home button destination</div>
                                <div className="text-sm text-muted-foreground">Navigator opens this page when you use the Home button.</div>
                              </div>
                              <div className="max-w-[18rem] truncate text-sm font-medium text-primary">{draftHomepage.trim() || 'https://duckduckgo.com/'}</div>
                            </div>
                            <div className={settingsRowClassName}>
                              <div>
                                <div className="font-medium">Address bar</div>
                                <div className="text-sm text-muted-foreground">You can type a website address or a search query directly.</div>
                              </div>
                              <div className="text-sm font-medium text-muted-foreground">Enabled</div>
                            </div>
                            <div className={settingsRowClassName}>
                              <div>
                                <div className="font-medium">Website theme preference</div>
                                <div className="text-sm text-muted-foreground">Controls whether websites prefer light, dark, or system colors.</div>
                              </div>
                              <div className="text-sm font-medium text-primary">{activeTheme.label}</div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  }

                  if (section.id === 'appearance') {
                    return (
                      <div key={section.id} className="space-y-4">
                        <div>
                          <h2 className="text-2xl font-semibold tracking-tight">Appearance</h2>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Control the preferred color-scheme hint MapleBrowser sends to websites.
                          </p>
                        </div>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Website theme</CardTitle>
                            <CardDescription>
                              This affects browser theme preference hints for web pages, not the MapleOS browser window chrome.
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="rounded-lg border border-dashed border-border/70 bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                              Changes are staged here and only applied when you click <span className="font-medium text-foreground">Save changes</span>. Some websites may still keep their own theme because of signed-in account settings or site-specific saved preferences.
                            </div>
                            <RadioGroup
                              value={draftTheme}
                              onValueChange={(value) => setDraftTheme(value as BrowserPageTheme)}
                              className="gap-3"
                            >
                              {themeOptions.map((option) => {
                                const OptionIcon = option.icon;
                                const inputId = `browser-theme-${option.value}`;

                                return (
                                  <Label
                                    key={option.value}
                                    htmlFor={inputId}
                                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted/30 p-4 transition-colors hover:bg-accent/50"
                                  >
                                    <RadioGroupItem id={inputId} value={option.value} className="mt-1" />
                                    <OptionIcon className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                    <div className="space-y-1">
                                      <div className="text-sm font-medium text-foreground">{option.label}</div>
                                      <div className="text-sm text-muted-foreground">{option.description}</div>
                                    </div>
                                  </Label>
                                );
                              })}
                            </RadioGroup>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  }

                  if (section.id === 'privacy') {
                    return (
                      <div key={section.id} className="space-y-4">
                        <div>
                          <h2 className="text-2xl font-semibold tracking-tight">Privacy & security</h2>
                          <p className="mt-1 text-sm text-muted-foreground">
                            How MapleBrowser applies browser-level preferences to websites and stores your browsing data.
                          </p>
                        </div>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Website rendering preferences</CardTitle>
                            <CardDescription>These controls affect browser hints sent to websites, not MapleOS itself.</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className={settingsRowClassName}>
                              <div>
                                <div className="font-medium">Page color preference</div>
                                <div className="text-sm text-muted-foreground">Websites may choose colors from this browser hint, but signed-in preferences and site-specific theme settings can override it.</div>
                              </div>
                              <div className="text-sm font-medium text-primary">{activeTheme.label}</div>
                            </div>
                            <div className={settingsRowClassName}>
                              <div>
                                <div className="font-medium">MapleOS window chrome</div>
                                <div className="text-sm text-muted-foreground">Browser controls keep following MapleOS styling separately from website content.</div>
                              </div>
                              <div className="text-sm font-medium text-muted-foreground">Separate</div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Navigator data</CardTitle>
                            <CardDescription>Open your saved history, bookmarks, and encrypted password vault from here.</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className={settingsRowClassName}>
                              <div className="pr-4">
                                <div className="font-medium">History & bookmarks</div>
                                <div className="text-sm text-muted-foreground">Review your recent browsing history and saved pages in the Navigator library.</div>
                              </div>
                              <Button type="button" variant="outline" onClick={handleOpenLibrary}>
                                <History className="mr-2 h-4 w-4" />
                                Open library
                              </Button>
                            </div>
                            <div className={settingsRowClassName}>
                              <div className="pr-4">
                                <div className="font-medium">Password vault</div>
                                <div className="text-sm text-muted-foreground">Create, unlock, and manage saved credentials in your encrypted local vault.</div>
                              </div>
                              <Button type="button" variant="outline" onClick={handleOpenPasswordManager}>
                                <KeyRound className="mr-2 h-4 w-4" />
                                Open vault
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  }

                  if (section.id === 'downloads') {
                    return (
                      <div key={section.id} className="space-y-4">
                        <div>
                          <h2 className="text-2xl font-semibold tracking-tight">Downloads</h2>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Saved files from MapleBrowser are stored inside the MapleOS file system.
                          </p>
                        </div>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Download behavior</CardTitle>
                            <CardDescription>Downloaded files are saved into MapleOS so they appear in the desktop file system.</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className={settingsRowClassName}>
                              <div>
                                <div className="font-medium">Save location</div>
                                <div className="text-sm text-muted-foreground">Completed downloads are written into the MapleOS downloads area.</div>
                              </div>
                              <div className="text-sm font-medium text-muted-foreground">MapleOS Downloads</div>
                            </div>
                            <div className={settingsRowClassName}>
                              <div>
                                <div className="font-medium">Download notifications</div>
                                <div className="text-sm text-muted-foreground">MapleBrowser shows a toast when a download finishes or fails.</div>
                              </div>
                              <div className="text-sm font-medium text-muted-foreground">Enabled</div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  }

                  return (
                    <div key={section.id} className="space-y-4">
                      <div>
                        <h2 className="text-2xl font-semibold tracking-tight">About MapleBrowser</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Technical details about the browser shell and current website appearance mode.
                        </p>
                      </div>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Browser details</CardTitle>
                          <CardDescription>MapleBrowser uses Electron&apos;s native browser engine for improved modern site compatibility.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className={settingsRowClassName}>
                            <div>
                              <div className="font-medium">Rendering engine</div>
                              <div className="text-sm text-muted-foreground">Website content is rendered through Electron instead of an in-page WASM browser.</div>
                            </div>
                            <div className="text-sm font-medium text-muted-foreground">Electron native</div>
                          </div>
                          <div className={settingsRowClassName}>
                            <div>
                              <div className="font-medium">Current website theme</div>
                              <div className="text-sm text-muted-foreground">The current preferred color-scheme hint sent to sites that respect browser theme preferences.</div>
                            </div>
                            <div className="text-sm font-medium text-primary">{activeTheme.label}</div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="flex items-center justify-between border-t border-border/70 bg-background px-6 py-4">
              <div className={saveError ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
                {saveError || (hasPendingChanges ? 'You have unsaved changes.' : 'All changes saved.')}
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSave} disabled={!hasPendingChanges || isSaving}>
                  {isSaving ? 'Saving...' : 'Save changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};